import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson } from '../api'
import { pageStyles } from '../styles'
import type { ThemeTokens } from '../theme'

/**
 * View B's agent matrix — overhaul item 10, the CACHED half of the Access page.
 *
 * WHY EVERY CELL CARRIES n/m, AND NOT A TICK. This is the design's whole
 * point. The MCP matrix above it draws a boolean because live resolution
 * makes the answer a boolean. This plane's answer cannot be a boolean, so it
 * is not drawn as one: LibreChat decides agent visibility from a `memberIds`
 * roster written at sign-in and never pruned, while the share itself is an
 * ACL row that does not care about the roster. A cell reading `3/1 !` says
 * "three people should see this, one actually can" without needing a
 * sentence, and `!` marks the rows where those two numbers part company.
 *
 * READ-ONLY. Cells are not clickable here, deliberately. Agent-share WRITES
 * from the matrix are item 17 (Later) and want their own audited write path;
 * a cell that looks like the LIVE matrix's clickable control but silently
 * does nothing would be worse than a cell that plainly shows a fact.
 *
 * REPAIR, STATED. In-Entra-only drift is repaired by the person signing out
 * and back in; there is no supported server-side cache refresh and one is
 * explicitly not being built. In-LibreChat-only (stale) drift is repaired by
 * a targeted removal.
 */

interface MatrixRow {
  group: string
  entraGroupId: string | null
  liveMemberCount: number | null
  cachedMemberCount: number
  drift: boolean
  cells: boolean[]
}

interface AgentMatrixData {
  available: boolean
  error: string | null
  agents: { name: string; agentId: string | null }[]
  rows: MatrixRow[]
  summary?: { driftingGroups: number; sharesTotal: number; sharesFullyVisible: number }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function AgentMatrix({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])
  const [data, setData] = useState<AgentMatrixData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await apiJson<AgentMatrixData>('/api/access/agents'))
    } catch (err) {
      setData(null)
      setError(errorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const cell: React.CSSProperties = {
    padding: '0.3rem 0.45rem',
    textAlign: 'center',
    borderBottom: `1px solid ${tokens.border}`,
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
  }
  const headCell: React.CSSProperties = {
    ...cell,
    textAlign: 'left',
    color: tokens.textSecondary,
    fontWeight: 600,
  }

  if (loading && !data) {
    return <p style={styles.hint}>Reading LibreChat’s agent ACL and every group’s live Entra roster…</p>
  }

  if (error) {
    return <p style={styles.error}>{error}</p>
  }

  // An unreadable plane is a STATED ABSENCE, never an empty grid. An empty
  // grid here would be a false all-clear of exactly the kind this overhaul
  // exists to remove.
  if (!data || !data.available) {
    return (
      <div style={{ ...styles.callout, borderLeft: `3px solid ${tokens.danger}` }}>
        <div>
          <strong>Not readable right now, so nothing is drawn here.</strong>
        </div>
        <div>{data?.error ?? 'The agent plane could not be read.'}</div>
        <div>
          What that costs you: this page cannot tell you that someone holds an agent share they cannot yet see, or
          still sees one they should have lost. Everything in the LIVE plane above is unaffected — that is the point
          of them being two planes.
        </div>
      </div>
    )
  }

  const summary = data.summary
  const unreadableLive = data.rows.filter((r) => r.liveMemberCount === null).length

  return (
    <>
      {summary && summary.driftingGroups > 0 ? (
        <div style={{ ...styles.callout, borderLeft: `3px solid ${tokens.danger}` }} role="alert">
          <strong>
            {summary.driftingGroups} group{summary.driftingGroups === 1 ? ' is' : 's are'} out of sync.{' '}
            {summary.sharesFullyVisible} of {summary.sharesTotal} shares are fully visible.
          </strong>
          <div style={{ color: tokens.textSecondary }}>
            Repair for in-Entra-only drift: the person signs out and back in. There is no supported server-side cache
            refresh and one is deliberately not being built. Repair for in-LibreChat-only (stale) membership: a
            targeted removal, one row.
          </div>
        </div>
      ) : null}

      {unreadableLive > 0 ? (
        <p style={styles.error}>
          {unreadableLive} group’s live Entra roster could not be read, so its count shows <code>?</code> rather than
          0. A 0 there would read as “nobody should see this”, which is the opposite of what an unreadable group
          means.
        </p>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', minWidth: '100%' }}>
          <thead>
            <tr>
              <th style={headCell}>Group</th>
              {data.agents.map((agent) => (
                <th key={agent.name} style={{ ...headCell, textAlign: 'center' }} title={agent.agentId ?? undefined}>
                  {agent.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => (
              <tr key={row.group}>
                <td style={headCell}>
                  {row.group}
                  {row.entraGroupId === null ? (
                    <span style={{ color: tokens.danger }}> · not linked to Entra</span>
                  ) : null}
                </td>
                {row.cells.map((granted, index) => (
                  <td
                    key={data.agents[index]?.name ?? index}
                    style={{ ...cell, color: row.drift ? tokens.danger : tokens.textSecondary }}
                  >
                    {granted
                      ? `${row.liveMemberCount ?? '?'}/${row.cachedMemberCount}${row.drift ? ' !' : ''}`
                      : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p style={{ ...styles.hint, marginTop: '0.6rem' }}>
        <code>n/m</code> = live Entra members / LibreChat cached members. <code>!</code> = they disagree, so{' '}
        <code>m−n</code> people hold this share and cannot see it until they sign in again. Blank = no ACL row. This
        grid is read-only: agent-share writes are a separate, audited change and are not built here.
      </p>
    </>
  )
}
